import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Eye,
  Plus,
  UserX,
  UserCheck,
  Ban,
  Mail
} from 'lucide-react';
import Badge from '../ui/Badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../ui/DropdownMenu';
import { apiService } from '../../services/api';
import { AddUserModal } from './AddUserModal';
import { EditUserModal } from './EditUserModal';
import { useToast } from '../ui/Toaster';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { UsersTableLoadingSkeleton } from '../ui/PageRouteSkeleton';

/** Matches TenantTableWithHeader / list tables: one card surface, theme vars track light/dark. */
const TeamTableShell = styled.div`
  font-family: var(--font-sans, inherit);
  width: 100%;
  max-width: 100%;
  border-radius: 16px;
  border: 1px solid var(--border);
  background: var(--card);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  width: 100%;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const Table = styled.table`
  width: 100%;
  min-width: 800px;
  border-collapse: separate;
  border-spacing: 0;
`;

const TableHeader = styled.thead``;

const TableHeaderRow = styled.tr`
  background: ${props =>
    `color-mix(in srgb, ${props.theme.muted} 70%, ${props.theme.card})`};
  border-bottom: 1px solid ${props => `color-mix(in srgb, ${props.theme.border} 80%, ${props.theme.card})`};
`;

const TableRow = styled.tr`
  background: transparent;
  transition: background 0.1s;

  &:hover {
    background: ${props =>
      `color-mix(in srgb, ${props.theme.muted} 65%, ${props.theme.card})`};
  }

`;

const TableHead = styled.th`
  padding: 11px 16px;
  text-align: left;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${props => props.theme.mutedForeground};
  background: transparent;

  &:first-child {
    padding-left: 24px;
  }

  &:last-child {
    padding-right: 24px;
  }
`;

const TableBody = styled.tbody`
  tr:last-child td {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 13px 16px;
  font-size: 13px;
  color: ${props => props.theme.foreground};
  vertical-align: middle;
  border-bottom: 1px solid ${props => `color-mix(in srgb, ${props.theme.border} 80%, ${props.theme.card})`};

  &:first-child {
    padding-left: 24px;
  }

  &:last-child {
    padding-right: 24px;
  }
`;

const ActionButton = styled.button`
  padding: 8px;
  height: auto;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: ${props => props.theme.foreground};
  
  &:hover {
    background: ${props => props.theme.muted};
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${props => `color-mix(in srgb, ${props.theme.border} 80%, ${props.theme.card})`};
  background: ${props =>
    `color-mix(in srgb, ${props.theme.muted} 55%, ${props.theme.card})`};
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.primary};
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.primary}10;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.foreground};
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin: 0 0 8px 0;
`;

const EmptyStateDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.mutedForeground};
  margin: 0;
`;

interface User {
  id: string;
  email: string;
  phone: string | null;
  role: string;
  accountStatus: string;
  lastLoginAt: string | null;
  createdAt: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  _count: {
    agentOf: number;
    caretakerOf: number;
    properties: number;
  };
}

export const UsersTable: React.FC = () => {
  const toast = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string>('LANDLORD');
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: string; email: string } | null>(null);
  const [userToUpdateStatus, setUserToUpdateStatus] = useState<{ id: string; newStatus: 'ACTIVE' | 'RESTRICTED' | 'SUSPENDED' } | null>(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);

  const fetchCurrentUser = async () => {
    try {
      const { data } = await apiService.getCurrentUser();
      const { resolveClientPmsRole } = await import('../../utils/orgMemberRole');
      const role = resolveClientPmsRole({
        membershipRole: data?.membership?.role,
        userRole: data?.user?.role,
        effectivePmsRole: data?.user?.effectivePmsRole,
      });
      setCurrentUserRole(role);
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await apiService.getTeamUsers();
      setUsers(data.users || []);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    setUserToDelete({ id: userId, email: userEmail });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeletingUserId(userToDelete.id);
      setError(null);
      await apiService.deleteTeamUser(userToDelete.id);

      toast.success('User deleted', {
        description: `${userToDelete.email} has been successfully removed.`,
      });

      // Refresh users list
      await fetchUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      const errorMsg = err?.response?.data?.error || err?.message || 'Failed to delete user';
      setError(errorMsg);
      toast.error('Failed to delete user', {
        description: errorMsg,
      });
    } finally {
      setDeletingUserId(null);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

    const statusMessages = {
    ACTIVE: 'activate',
    RESTRICTED: 'restrict',
    SUSPENDED: 'suspend'
  };

  const handleResendInvite = async (userId: string) => {
    try {
      setResendingUserId(userId);
      setError(null);
      const { data } = await apiService.resendTeamInvitation(userId);
      toast.success('Invitation resent', {
        description: data?.message || 'Invite link sent by email and SMS (if phone on file).',
      });
      await fetchUsers();
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Failed to resend invitation';
      toast.error('Resend failed', { description: errorMsg });
    } finally {
      setResendingUserId(null);
    }
  };

  const handleUpdateUserStatus = async (userId: string, newStatus: 'ACTIVE' | 'RESTRICTED' | 'SUSPENDED') => {
    setUserToUpdateStatus({ id: userId, newStatus });
    setShowStatusConfirm(true);
  };

  const handleConfirmUpdateStatus = async () => {
    if (!userToUpdateStatus) return;

    try {
      setUpdatingUserId(userToUpdateStatus.id);
      setError(null);
      await apiService.updateTeamUserStatus(userToUpdateStatus.id, userToUpdateStatus.newStatus);

      toast.success('User status updated', {
        description: `User has been ${statusMessages[userToUpdateStatus.newStatus]}ed successfully.`,
      });

      // Refresh users list
      await fetchUsers();
    } catch (err: any) {
      console.error('Error updating user status:', err);
      const errorMsg = err?.response?.data?.error || err?.message || 'Failed to update user status';
      setError(errorMsg);
      toast.error('Failed to update user status', {
        description: errorMsg,
      });
    } finally {
      setUpdatingUserId(null);
      setShowStatusConfirm(false);
      setUserToUpdateStatus(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'AGENT':
        return 'secondary';
      case 'CARETAKER':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'default';
      case 'RESTRICTED':
        return 'secondary';
      case 'SUSPENDED':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getUserDisplayName = (user: User) => {
    return user.email.split('@')[0];
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setIsViewMode(true);
    setIsEditUserModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsViewMode(false);
    setIsEditUserModalOpen(true);
  };

  return (
    <>
      <TeamTableShell>
      <HeaderContainer>
        <AddButton onClick={() => setIsAddUserModalOpen(true)}>
          <Plus size={16} />
          Add User
        </AddButton>
      </HeaderContainer>
      <TableContainer>
        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead style={{ textAlign: 'right' }}>Actions</TableHead>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} style={{ padding: 0, verticalAlign: 'top' }}>
                  <UsersTableLoadingSkeleton />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <EmptyState>
                    <EmptyStateTitle>Error</EmptyStateTitle>
                    <EmptyStateDescription>{error}</EmptyStateDescription>
                  </EmptyState>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <EmptyState>
                    <EmptyStateTitle>No sub-users found</EmptyStateTitle>
                    <EmptyStateDescription>
                      You haven't invited any agents or caretakers yet. Click "Add User" to invite a new team member.
                    </EmptyStateDescription>
                  </EmptyState>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <UserInfo>
                      <UserName>{getUserDisplayName(user)}</UserName>
                      {user.phone && (
                        <UserEmail>{user.phone}</UserEmail>
                      )}
                    </UserInfo>
                  </TableCell>
                  <TableCell>
                    <UserEmail>{user.email}</UserEmail>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getRoleBadgeVariant(user.role)}>
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(user.accountStatus)}>
                      {user.accountStatus}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <UserEmail>{formatDate(user.lastLoginAt)}</UserEmail>
                  </TableCell>
                  <TableCell style={{ textAlign: 'right' }}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <ActionButton>
                          <MoreHorizontal size={16} />
                        </ActionButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewUser(user)}>
                          <Eye size={16} style={{ marginRight: 8 }} />
                          View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditUser(user)}>
                          <Edit size={16} style={{ marginRight: 8 }} />
                          Edit
                        </DropdownMenuItem>
                        {user.accountStatus !== 'ACTIVE' && (
                          <DropdownMenuItem
                            onClick={() => handleResendInvite(user.id)}
                            disabled={resendingUserId === user.id}
                          >
                            <Mail size={16} style={{ marginRight: 8 }} />
                            {resendingUserId === user.id ? 'Sending...' : 'Resend invite'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {user.accountStatus === 'ACTIVE' ? (
                          <>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateUserStatus(user.id, 'RESTRICTED')}
                              disabled={updatingUserId === user.id}
                            >
                              <Ban size={16} style={{ marginRight: 8 }} />
                              {updatingUserId === user.id ? 'Restricting...' : 'Restrict'}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateUserStatus(user.id, 'SUSPENDED')}
                              disabled={updatingUserId === user.id}
                            >
                              <UserX size={16} style={{ marginRight: 8 }} />
                              {updatingUserId === user.id ? 'Suspending...' : 'Suspend'}
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <DropdownMenuItem 
                            onClick={() => handleUpdateUserStatus(user.id, 'ACTIVE')}
                            disabled={updatingUserId === user.id}
                          >
                            <UserCheck size={16} style={{ marginRight: 8 }} />
                            {updatingUserId === user.id ? 'Activating...' : 'Activate'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          variant="destructive"
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          disabled={deletingUserId === user.id}
                        >
                          <Trash2 size={16} style={{ marginRight: 8 }} />
                          {deletingUserId === user.id ? 'Deleting...' : 'Delete'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </TeamTableShell>

      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSuccess={fetchUsers}
        currentUserRole={currentUserRole}
      />
      <EditUserModal
        isOpen={isEditUserModalOpen}
        onClose={() => {
          setIsEditUserModalOpen(false);
          setSelectedUser(null);
          setIsViewMode(false);
        }}
        onSuccess={fetchUsers}
        user={selectedUser}
        currentUserRole={currentUserRole}
        viewOnly={isViewMode}
      />

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={handleConfirmDeleteUser}
        title="Delete User"
        description={userToDelete ? `Are you sure you want to delete ${userToDelete.email}? This action cannot be undone.` : ''}
        confirmLabel="Delete"
        variant="danger"
        isLoading={deletingUserId !== null}
      />

      <ConfirmationDialog
        isOpen={showStatusConfirm}
        onCancel={() => {
          setShowStatusConfirm(false);
          setUserToUpdateStatus(null);
        }}
        onConfirm={handleConfirmUpdateStatus}
        title="Update User Status"
        description={userToUpdateStatus ? `Are you sure you want to ${statusMessages[userToUpdateStatus.newStatus]} this user?` : ''}
        confirmLabel="Confirm"
        variant="default"
        isLoading={updatingUserId !== null}
      />
    </>
  );
};

